/**
 * Edit Crew Page
 *
 * Admin page for editing an existing crew member.
 */

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getUser, type UserDetail } from '../../../api/users';
import { CrewForm } from '../../../components/crew';
import { getErrorMessage } from '../../../api/client';

export default function EditCrewPage() {
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;

    const loadUser = async () => {
      try {
        setLoading(true);
        const { user } = await getUser(id);
        setUser(user);
      } catch (err) {
        setError(getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, [id]);

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-24 mb-4"></div>
        <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div className="h-10 bg-gray-200 rounded"></div>
              <div className="h-10 bg-gray-200 rounded"></div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="h-10 bg-gray-200 rounded"></div>
              <div className="h-10 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">{error || 'User not found'}</div>
        <Link to="/admin/crew" className="text-primary-600 hover:text-primary-500">
          Back to Crew List
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb */}
      <nav className="mb-4 text-sm">
        <ol className="flex items-center space-x-2">
          <li>
            <Link to="/admin/crew" className="text-gray-500 hover:text-gray-700">
              Crew
            </Link>
          </li>
          <li>
            <span className="text-gray-400">/</span>
          </li>
          <li>
            <Link to={`/admin/crew/${user.id}`} className="text-gray-500 hover:text-gray-700">
              {user.firstName} {user.lastName}
            </Link>
          </li>
          <li>
            <span className="text-gray-400">/</span>
          </li>
          <li className="text-gray-900 font-medium">Edit</li>
        </ol>
      </nav>

      {/* Page header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">
          Edit {user.firstName} {user.lastName}
        </h1>
        <p className="text-gray-600 mt-1">
          Update crew member information and settings.
        </p>
      </div>

      {/* Form */}
      <div className="max-w-2xl">
        <CrewForm userId={id} />
      </div>
    </div>
  );
}
